import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Separator } from './ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Skeleton } from './ui/skeleton';
import { toast } from 'sonner';
import {
  Users, MapPin, Clock, Phone, Mail, Navigation, Plus,
  Truck, CheckCircle, AlertCircle, Wrench, Eye, ArrowLeft,
  RotateCcw, Route, Zap, Home, User, Clipboard, ExternalLink, Copy
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

/* ──────────────────────────────────────────────
   STATUS HELPERS
   ────────────────────────────────────────────── */
const statusConfig = {
  available: { label: 'Available', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  en_route: { label: 'En Route', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Truck },
  on_site: { label: 'On Site', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: Wrench },
  off_duty: { label: 'Off Duty', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30', icon: User },
  pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
  assigned: { label: 'Assigned', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: User },
  in_progress: { label: 'In Progress', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: Wrench },
  completed: { label: 'Completed', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertCircle },
};

const StatusBadge = ({ status }) => {
  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;
  return (
    <Badge variant="outline" className={`${config.color} border text-xs font-medium`} data-testid={`status-badge-${status}`}>
      <Icon className="h-3 w-3 mr-1" />
      {config.label}
    </Badge>
  );
};

/* ──────────────────────────────────────────────
   STREET VIEW COMPONENT
   ────────────────────────────────────────────── */
const StreetView = ({ lat, lng, address, size = 'md' }) => {
  const dims = size === 'sm' ? { w: 200, h: 120 } : size === 'lg' ? { w: 600, h: 300 } : { w: 400, h: 200 };
  const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '';
  const location = lat && lng ? `${lat},${lng}` : address || '';
  if (!location) return null;
  const src = `https://www.google.com/maps/embed/v1/streetview?key=${apiKey}&location=${encodeURIComponent(location)}&heading=210&pitch=10&fov=90`;
  return (
    <div className="rounded-lg overflow-hidden border border-slate-700" data-testid="street-view-preview">
      <iframe
        title="Street View"
        width={dims.w}
        height={dims.h}
        style={{ border: 0, width: '100%', height: dims.h }}
        loading="lazy"
        allowFullScreen
        referrerPolicy="no-referrer-when-downgrade"
        src={src}
      />
    </div>
  );
};

/* ──────────────────────────────────────────────
   MAP EMBED COMPONENT (route or location)
   ────────────────────────────────────────────── */
const MapEmbed = ({ origin, destination, height = 250 }) => {
  const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '';
  if (!origin || !destination) return null;
  const src = `https://www.google.com/maps/embed/v1/directions?key=${apiKey}&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}&mode=driving`;
  return (
    <div className="rounded-lg overflow-hidden border border-slate-700" data-testid="map-embed">
      <iframe
        title="Route Map"
        width="100%"
        height={height}
        style={{ border: 0 }}
        loading="lazy"
        allowFullScreen
        referrerPolicy="no-referrer-when-downgrade"
        src={src}
      />
    </div>
  );
};

/* ══════════════════════════════════════════════
   DISPATCH DASHBOARD
   ══════════════════════════════════════════════ */
export const DispatchDashboard = () => {
  const [technicians, setTechnicians] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTech, setShowNewTech] = useState(false);
  const [showNewJob, setShowNewJob] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [dispatchingJob, setDispatchingJob] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // New technician form
  const [newTech, setNewTech] = useState({ name: '', phone: '', email: '', current_address: '' });
  // New job form
  const [newJob, setNewJob] = useState({ customer_name: '', customer_phone: '', customer_email: '', address: '', service_type: '', description: '' });

  const fetchData = useCallback(async () => {
    try {
      const [techRes, jobRes] = await Promise.all([
        fetch(`${API}/api/technicians`),
        fetch(`${API}/api/jobs`)
      ]);
      const techs = await techRes.json();
      const allJobs = await jobRes.json();
      setTechnicians(Array.isArray(techs) ? techs : []);
      setJobs(Array.isArray(allJobs) ? allJobs : []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load dispatch data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(() => setRefreshKey(k => k + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  const handleCreateTech = async (e) => {
    e.preventDefault();
    if (!newTech.name || !newTech.phone) { toast.error('Name and phone are required'); return; }
    try {
      const res = await fetch(`${API}/api/technicians`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTech)
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Technician ${data.name} registered`);
        setNewTech({ name: '', phone: '', email: '', current_address: '' });
        setShowNewTech(false);
        setRefreshKey(k => k + 1);
      } else {
        toast.error('Failed to create technician');
      }
    } catch (err) { toast.error('Error creating technician'); }
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    if (!newJob.customer_name || !newJob.customer_phone || !newJob.address || !newJob.service_type) {
      toast.error('Please fill in all required fields'); return;
    }
    try {
      const res = await fetch(`${API}/api/jobs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newJob)
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Job created. Tracking: ${data.tracking_token}`);
        setNewJob({ customer_name: '', customer_phone: '', customer_email: '', address: '', service_type: '', description: '' });
        setShowNewJob(false);
        setRefreshKey(k => k + 1);
      } else {
        toast.error('Failed to create job');
      }
    } catch (err) { toast.error('Error creating job'); }
  };

  const handleDispatch = async (jobId, techId) => {
    setDispatchingJob(jobId);
    try {
      const res = await fetch(`${API}/api/dispatch`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id: jobId, technician_id: techId })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Dispatched ${data.technician} — ETA: ${data.eta_text} (${data.distance})`);
        setRefreshKey(k => k + 1);
      } else {
        toast.error(data.detail || 'Dispatch failed');
      }
    } catch (err) { toast.error('Error dispatching'); }
    finally { setDispatchingJob(null); }
  };

  const handleJobAction = async (jobId, action) => {
    try {
      const res = await fetch(`${API}/api/jobs/${jobId}/${action}`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success(`Job marked as ${data.status}`);
        setRefreshKey(k => k + 1);
        setSelectedJob(null);
      }
    } catch (err) { toast.error(`Error: ${action}`); }
  };

  const copyTrackingLink = (token) => {
    const url = `${window.location.origin}/track?token=${token}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Tracking link copied!')).catch(() => toast.error('Failed to copy'));
  };

  const pendingJobs = jobs.filter(j => j.status === 'pending');
  const activeJobs = jobs.filter(j => ['assigned', 'en_route', 'in_progress'].includes(j.status));
  const completedJobs = jobs.filter(j => j.status === 'completed');
  const availableTechs = technicians.filter(t => t.status === 'available');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-12 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="dispatch-dashboard">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50 sticky top-0 z-40 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="text-slate-400 hover:text-white transition-colors" data-testid="back-to-site">
              <ArrowLeft className="h-5 w-5" />
            </a>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Navigation className="h-5 w-5 text-orange-400" />
                Dispatch Dashboard
              </h1>
              <p className="text-xs text-slate-500">RemodWorks Field Service Management</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setRefreshKey(k => k + 1)} data-testid="refresh-btn">
              <RotateCcw className="h-4 w-4 mr-1" /> Refresh
            </Button>
            <Button size="sm" className="btn-gradient-orange text-white" onClick={() => setShowNewJob(true)} data-testid="new-job-btn">
              <Plus className="h-4 w-4 mr-1" /> New Job
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowNewTech(true)} data-testid="new-tech-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Tech
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Technicians', value: technicians.length, sub: `${availableTechs.length} available`, icon: Users, color: 'text-blue-400' },
            { label: 'Pending Jobs', value: pendingJobs.length, sub: 'Awaiting dispatch', icon: Clock, color: 'text-yellow-400' },
            { label: 'Active Jobs', value: activeJobs.length, sub: 'In progress', icon: Truck, color: 'text-orange-400' },
            { label: 'Completed', value: completedJobs.length, sub: 'Today', icon: CheckCircle, color: 'text-emerald-400' },
          ].map((stat, i) => (
            <Card key={i} className="border-slate-800 bg-slate-900/50" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`h-10 w-10 rounded-lg bg-slate-800 flex items-center justify-center ${stat.color}`}>
                  <stat.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stat.value}</div>
                  <div className="text-xs text-slate-500">{stat.sub}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="jobs" className="space-y-4">
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="jobs" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400">
              <Clipboard className="h-4 w-4 mr-1" /> Jobs ({jobs.length})
            </TabsTrigger>
            <TabsTrigger value="technicians" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400">
              <Users className="h-4 w-4 mr-1" /> Technicians ({technicians.length})
            </TabsTrigger>
          </TabsList>

          {/* JOBS TAB */}
          <TabsContent value="jobs" className="space-y-4">
            {/* Pending Jobs */}
            {pendingJobs.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-yellow-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4" /> Pending — Needs Dispatch ({pendingJobs.length})
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {pendingJobs.map(job => (
                    <JobCard key={job.id} job={job} technicians={availableTechs}
                      onDispatch={handleDispatch} onSelect={setSelectedJob}
                      dispatching={dispatchingJob === job.id}
                      onCopyLink={copyTrackingLink} />
                  ))}
                </div>
              </div>
            )}

            {/* Active Jobs */}
            {activeJobs.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-orange-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Truck className="h-4 w-4" /> Active Jobs ({activeJobs.length})
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {activeJobs.map(job => (
                    <JobCard key={job.id} job={job} technicians={[]}
                      onDispatch={handleDispatch} onSelect={setSelectedJob}
                      onAction={handleJobAction} onCopyLink={copyTrackingLink} />
                  ))}
                </div>
              </div>
            )}

            {/* Completed */}
            {completedJobs.length > 0 && (
              <Accordion type="single" collapsible>
                <AccordionItem value="completed" className="border-slate-800">
                  <AccordionTrigger className="text-sm font-semibold text-emerald-400 uppercase tracking-wider hover:no-underline">
                    <span className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" /> Completed ({completedJobs.length})
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-2">
                      {completedJobs.map(job => (
                        <JobCard key={job.id} job={job} technicians={[]}
                          onSelect={setSelectedJob} onCopyLink={copyTrackingLink} />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}

            {jobs.length === 0 && (
              <Card className="border-slate-800 bg-slate-900/30">
                <CardContent className="p-12 text-center">
                  <Clipboard className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-400 mb-2">No Jobs Yet</h3>
                  <p className="text-sm text-slate-500 mb-4">Create your first service job to get started.</p>
                  <Button className="btn-gradient-orange text-white" onClick={() => setShowNewJob(true)}>
                    <Plus className="h-4 w-4 mr-1" /> Create Job
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* TECHNICIANS TAB */}
          <TabsContent value="technicians" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {technicians.map(tech => (
                <Card key={tech.id} className="border-slate-800 bg-slate-900/50 hover:border-slate-700 transition-colors" data-testid={`tech-card-${tech.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-white">{tech.name}</h4>
                        <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                          <Phone className="h-3 w-3" /> {tech.phone}
                        </div>
                        {tech.email && (
                          <div className="flex items-center gap-1 text-xs text-slate-500">
                            <Mail className="h-3 w-3" /> {tech.email}
                          </div>
                        )}
                      </div>
                      <StatusBadge status={tech.status} />
                    </div>
                    {tech.current_address && (
                      <div className="flex items-start gap-2 text-xs text-slate-400 mt-2 p-2 bg-slate-800/50 rounded">
                        <MapPin className="h-3 w-3 flex-shrink-0 mt-0.5 text-orange-400" />
                        <span>{tech.current_address}</span>
                      </div>
                    )}
                    {tech.active_job_id && (
                      <div className="mt-2 text-xs text-blue-400 flex items-center gap-1">
                        <Truck className="h-3 w-3" /> Active Job: {tech.active_job_id.slice(0, 8)}...
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
            {technicians.length === 0 && (
              <Card className="border-slate-800 bg-slate-900/30">
                <CardContent className="p-12 text-center">
                  <Users className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-400 mb-2">No Technicians</h3>
                  <p className="text-sm text-slate-500 mb-4">Register your first technician.</p>
                  <Button variant="outline" onClick={() => setShowNewTech(true)}>
                    <Plus className="h-4 w-4 mr-1" /> Add Technician
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* ── Job Detail Modal ──────────────────── */}
      <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 text-white max-h-[85vh] overflow-y-auto">
          {selectedJob && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Wrench className="h-5 w-5 text-orange-400" />
                    {selectedJob.service_type}
                  </span>
                  <StatusBadge status={selectedJob.status} />
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                {/* Customer Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Customer</Label>
                    <p className="text-sm font-medium">{selectedJob.customer_name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Phone</Label>
                    <p className="text-sm">{selectedJob.customer_phone}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Address</Label>
                  <p className="text-sm flex items-start gap-1"><MapPin className="h-3 w-3 mt-1 text-orange-400 flex-shrink-0" />{selectedJob.address}</p>
                </div>
                {selectedJob.description && (
                  <div>
                    <Label className="text-xs text-slate-500">Description</Label>
                    <p className="text-sm text-slate-300">{selectedJob.description}</p>
                  </div>
                )}

                {/* ETA & Route Info */}
                {selectedJob.eta_text && (
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                      <Clock className="h-4 w-4 text-blue-400 mx-auto mb-1" />
                      <div className="text-lg font-bold">{selectedJob.eta_text}</div>
                      <div className="text-xs text-slate-500">ETA</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                      <Route className="h-4 w-4 text-orange-400 mx-auto mb-1" />
                      <div className="text-lg font-bold">{selectedJob.distance_text || '—'}</div>
                      <div className="text-xs text-slate-500">Distance</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                      <User className="h-4 w-4 text-emerald-400 mx-auto mb-1" />
                      <div className="text-sm font-bold">{selectedJob.assigned_technician_name || '—'}</div>
                      <div className="text-xs text-slate-500">Technician</div>
                    </div>
                  </div>
                )}

                {/* Street View */}
                {(selectedJob.lat || selectedJob.address) && (
                  <div>
                    <Label className="text-xs text-slate-500 mb-2 block">Property Preview</Label>
                    <StreetView lat={selectedJob.lat} lng={selectedJob.lng} address={selectedJob.address} size="lg" />
                  </div>
                )}

                {/* Route Map */}
                {selectedJob.assigned_technician_id && selectedJob.status === 'en_route' && (
                  <div>
                    <Label className="text-xs text-slate-500 mb-2 block">Route</Label>
                    <MapEmbed
                      origin={technicians.find(t => t.id === selectedJob.assigned_technician_id)?.current_address || ''}
                      destination={selectedJob.address}
                      height={250}
                    />
                  </div>
                )}

                {/* Tracking Link */}
                <div className="flex items-center gap-2 p-3 bg-slate-800/50 rounded-lg">
                  <ExternalLink className="h-4 w-4 text-orange-400 flex-shrink-0" />
                  <span className="text-xs text-slate-400 flex-1 truncate">
                    {window.location.origin}/track?token={selectedJob.tracking_token}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => copyTrackingLink(selectedJob.tracking_token)} data-testid="copy-tracking-link">
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  {selectedJob.status === 'en_route' && (
                    <Button className="flex-1 bg-orange-600 hover:bg-orange-700 text-white" onClick={() => handleJobAction(selectedJob.id, 'arrive')} data-testid="mark-arrived-btn">
                      <MapPin className="h-4 w-4 mr-1" /> Mark Arrived
                    </Button>
                  )}
                  {selectedJob.status === 'in_progress' && (
                    <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleJobAction(selectedJob.id, 'complete')} data-testid="mark-complete-btn">
                      <CheckCircle className="h-4 w-4 mr-1" /> Mark Complete
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── New Technician Modal ──────────────── */}
      <Dialog open={showNewTech} onOpenChange={setShowNewTech}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-orange-400" /> Register Technician</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTech} className="space-y-4 mt-2">
            <div>
              <Label className="text-sm text-slate-300">Name *</Label>
              <Input value={newTech.name} onChange={e => setNewTech({...newTech, name: e.target.value})} placeholder="Mike Johnson" className="mt-1" data-testid="new-tech-name" />
            </div>
            <div>
              <Label className="text-sm text-slate-300">Phone *</Label>
              <Input value={newTech.phone} onChange={e => setNewTech({...newTech, phone: e.target.value})} placeholder="234-205-1010" className="mt-1" data-testid="new-tech-phone" />
            </div>
            <div>
              <Label className="text-sm text-slate-300">Email</Label>
              <Input value={newTech.email} onChange={e => setNewTech({...newTech, email: e.target.value})} placeholder="mike@remodworks.com" className="mt-1" data-testid="new-tech-email" />
            </div>
            <div>
              <Label className="text-sm text-slate-300">Current Location / Address</Label>
              <Input value={newTech.current_address} onChange={e => setNewTech({...newTech, current_address: e.target.value})} placeholder="100 S Main St, Akron, OH" className="mt-1" data-testid="new-tech-address" />
            </div>
            <Button type="submit" className="w-full btn-gradient-orange text-white" data-testid="submit-new-tech">Register Technician</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── New Job Modal ──────────────────── */}
      <Dialog open={showNewJob} onOpenChange={setShowNewJob}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Clipboard className="h-5 w-5 text-orange-400" /> Create Service Job</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateJob} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-300">Customer Name *</Label>
                <Input value={newJob.customer_name} onChange={e => setNewJob({...newJob, customer_name: e.target.value})} placeholder="Jane Smith" className="mt-1" data-testid="new-job-name" />
              </div>
              <div>
                <Label className="text-xs text-slate-300">Phone *</Label>
                <Input value={newJob.customer_phone} onChange={e => setNewJob({...newJob, customer_phone: e.target.value})} placeholder="330-555-1234" className="mt-1" data-testid="new-job-phone" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-300">Address *</Label>
              <Input value={newJob.address} onChange={e => setNewJob({...newJob, address: e.target.value})} placeholder="456 Market Ave, Canton, OH" className="mt-1" data-testid="new-job-address" />
            </div>
            <div>
              <Label className="text-xs text-slate-300">Service Type *</Label>
              <Input value={newJob.service_type} onChange={e => setNewJob({...newJob, service_type: e.target.value})} placeholder="Appliance Repair" className="mt-1" data-testid="new-job-service" />
            </div>
            <div>
              <Label className="text-xs text-slate-300">Description</Label>
              <Input value={newJob.description} onChange={e => setNewJob({...newJob, description: e.target.value})} placeholder="Dryer not heating, needs diagnosis" className="mt-1" data-testid="new-job-description" />
            </div>
            <div>
              <Label className="text-xs text-slate-300">Email</Label>
              <Input value={newJob.customer_email} onChange={e => setNewJob({...newJob, customer_email: e.target.value})} placeholder="jane@email.com" className="mt-1" data-testid="new-job-email" />
            </div>
            <Button type="submit" className="w-full btn-gradient-orange text-white" data-testid="submit-new-job">Create Job</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ──────────────────────────────────────────────
   JOB CARD COMPONENT
   ────────────────────────────────────────────── */
const JobCard = ({ job, technicians = [], onDispatch, onSelect, onAction, dispatching, onCopyLink }) => {
  const [selectedTech, setSelectedTech] = useState('');

  return (
    <Card className="border-slate-800 bg-slate-900/50 hover:border-slate-700 transition-colors" data-testid={`job-card-${job.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-white text-sm truncate">{job.customer_name}</h4>
              <StatusBadge status={job.status} />
            </div>
            <p className="text-xs text-orange-400 font-medium">{job.service_type}</p>
          </div>
          <div className="flex items-center gap-1 ml-2">
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onCopyLink && onCopyLink(job.tracking_token)} title="Copy tracking link">
              <Copy className="h-3 w-3 text-slate-400" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onSelect && onSelect(job)} title="View details" data-testid={`view-job-${job.id}`}>
              <Eye className="h-3 w-3 text-slate-400" />
            </Button>
          </div>
        </div>

        <div className="flex items-start gap-1 text-xs text-slate-400 mb-2">
          <MapPin className="h-3 w-3 flex-shrink-0 mt-0.5 text-slate-500" />
          <span className="truncate">{job.address}</span>
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
          <Phone className="h-3 w-3" /> {job.customer_phone}
        </div>

        {/* ETA info for active jobs */}
        {job.eta_text && (
          <div className="flex items-center gap-3 text-xs mb-3 p-2 bg-blue-500/10 rounded border border-blue-500/20">
            <div className="flex items-center gap-1 text-blue-400">
              <Clock className="h-3 w-3" /> {job.eta_text}
            </div>
            {job.distance_text && (
              <div className="flex items-center gap-1 text-slate-400">
                <Route className="h-3 w-3" /> {job.distance_text}
              </div>
            )}
            {job.assigned_technician_name && (
              <div className="flex items-center gap-1 text-emerald-400">
                <User className="h-3 w-3" /> {job.assigned_technician_name}
              </div>
            )}
          </div>
        )}

        {/* Dispatch controls for pending jobs */}
        {job.status === 'pending' && technicians.length > 0 && (
          <div className="flex items-center gap-2 mt-2">
            <Select value={selectedTech} onValueChange={setSelectedTech}>
              <SelectTrigger className="flex-1 h-8 text-xs bg-slate-800 border-slate-700" data-testid={`select-tech-${job.id}`}>
                <SelectValue placeholder="Select technician" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {technicians.map(t => (
                  <SelectItem key={t.id} value={t.id} className="text-xs text-white">
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              className="h-8 btn-gradient-orange text-white text-xs"
              disabled={!selectedTech || dispatching}
              onClick={() => onDispatch && onDispatch(job.id, selectedTech)}
              data-testid={`dispatch-btn-${job.id}`}
            >
              <Zap className="h-3 w-3 mr-1" />
              {dispatching ? '...' : 'Dispatch'}
            </Button>
          </div>
        )}

        {/* Action buttons for active jobs */}
        {job.status === 'en_route' && onAction && (
          <Button size="sm" className="w-full h-8 text-xs bg-orange-600 hover:bg-orange-700 text-white mt-2" onClick={() => onAction(job.id, 'arrive')}>
            <MapPin className="h-3 w-3 mr-1" /> Mark Arrived
          </Button>
        )}
        {job.status === 'in_progress' && onAction && (
          <Button size="sm" className="w-full h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white mt-2" onClick={() => onAction(job.id, 'complete')}>
            <CheckCircle className="h-3 w-3 mr-1" /> Mark Complete
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
